import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CognitoUser, UserAttributes } from '../../model/user-admin.model';
import { UserAdminService } from '../../service/user-admin.service';

export interface UserDialogData {
    mode: 'create' | 'edit';
    user?: CognitoUser;
}

@Component({
    selector: 'app-user-admin-dialog',
    templateUrl: './user-admin-dialog.component.html',
    standalone: false
})
export class UserAdminDialogComponent implements OnInit {
    userForm: FormGroup;
    isEditMode: boolean;
    accessOptions = ['SU', 'GA', 'QA', 'PI'];
    submitting = false;
    errorMessage = '';

    constructor(
        public dialogRef: MatDialogRef<UserAdminDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UserDialogData,
        private fb: FormBuilder,
        private userAdminService: UserAdminService
    ) {
        this.isEditMode = data.mode === 'edit';
    }

    ngOnInit(): void {
        this.userForm = this.fb.group({
            username: [
                { value: this.data.user?.username || '', disabled: this.isEditMode },
                this.isEditMode ? [] : [Validators.required]
            ],
            email: [this.data.user?.email || '', [Validators.required, Validators.email]],
            name: [this.data.user?.name || '', Validators.required],
            organization: [this.data.user?.organization || '', Validators.required],
            group: [this.data.user?.group || '', Validators.required],
            access: [this.data.user?.access || 'GA', Validators.required]
        });
    }

    onCancel(): void {
        if (this.submitting) {
            return;
        }
        this.dialogRef.close(null);
    }

    onSave(): void {
        if (!this.userForm.valid || this.submitting) {
            return;
        }
        const v = this.userForm.getRawValue();
        const userAttributes: UserAttributes = {
            email: v.email,
            name: v.name,
            organization: v.organization,
            group: v.group,
            access: v.access
        };

        this.submitting = true;
        this.errorMessage = '';

        const request$ = this.isEditMode && this.data.user
            ? this.userAdminService.updateUser(this.data.user.username, userAttributes)
            : this.userAdminService.createUser(v.username, userAttributes);

        request$.subscribe({
            next: () => {
                this.submitting = false;
                this.dialogRef.close(true);
            },
            error: (err) => {
                this.submitting = false;
                this.errorMessage = this.formatError(err);
            }
        });
    }

    private formatError(err: { status?: number }): string {
        switch (err && err.status) {
            case 400: return 'Bad Request. Please verify the input values.';
            case 401: return 'Unauthorized. Please login again.';
            case 403: return 'Access denied. You do not have permission to perform this action.';
            case 404: return 'Resource not found.';
            case 409: return 'Conflict. The user may already exist.';
            case 500: return 'Server error. Please try again later.';
            case 502: return 'Bad Gateway. The server is temporarily unavailable.';
            case 503: return 'Service Unavailable. Please try again later.';
            case 504: return 'Gateway Timeout. Server took too long to respond.';
            default:  return 'Unexpected error occurred. Please try again.';
        }
    }
}